import {
  DiscountAmoutType,
  DiscountApplyForType,
  DiscountType,
  FlightDiscountForm,
} from "@/types";
import { ROUTE } from "@/types/router";
import {
  CircularProgress,
  IconButton,
  Theme,
  useMediaQuery,
} from "@mui/material";
import { useFormik } from "formik";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { cn } from "@/utils/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ChevronDown } from "lucide-react";
import useSelectDate from "@/hooks/useSelectDate";
import dayjs from "dayjs";
import SelectDiscountDate from "../createDiscount/componets/SelectDiscountDate";
import { createDiscountService } from "@/services/discount.service";

const initialValues: FlightDiscountForm = {
  discountName: "",
  discountCode: "",
  discountValue: 0,
  discountType: DiscountType.PERCENTAGE,
  startDate: "",
  endDate: "",
  active: true,
};

const validationSchema = Yup.object().shape({
  discountName: Yup.string().required("Discount name is required"),
  discountCode: Yup.string().required("Discount code is required"),
  usageLimit: Yup.number()
    .typeError("")
    .min(1, "Usage limit must be greater than 0")
    .optional()
    .nullable(),
  usageLimitPerUser: Yup.number()
    .typeError("")
    .min(1, "Usage per user must be greater than 0")
    .optional()
    .nullable(),
  minimumSpend: Yup.number()
    .typeError("")
    .min(0, "Minimum spend must be greater than or equal to 0")
    .optional()
    .nullable(),
  startDate: Yup.string().required("Valid date is required"),
  endDate: Yup.string().required("Valid date is required"),
  discountValue: Yup.number()
    .typeError("")
    .min(0, "Discount amount must be greater than 0")
    .required("Discount amount is required"),
  maximumDiscountValue: Yup.number()
    .typeError("")
    .min(0, "Maximum discount must be greater than 0")
    .optional()
    .nullable(),
  discountType: Yup.string().required("Discount type is required"),
  // discountCities: Yup.array().optional(),
});

const CreateFlightDiscountPage = () => {
  const navigation = useNavigate();
  const [isLoading, setLoading] = useState<boolean>(false);
  const [selectedDiscountType, setSelectedDiscountType] =
    useState<DiscountAmoutType>(DiscountAmoutType.PERCENTAGE);
  const [isOpen, setIsOpen] = useState(false);
  const refForm = useRef<FlightDiscountForm>(initialValues);
  const { date, ...propsDate } = useSelectDate();
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm"),
  );
  const initial = useMemo(
    () => ({
      ...initialValues,
      startDate: date?.from?.toISOString() || "",
      endDate: date?.to?.toISOString() || "",
    }),
    [date?.from, date?.to],
  );

  const handleCreateDiscount = useCallback(
    async (formData: FlightDiscountForm) => {
      try {
        setLoading(true);

        const formDataToSubmit = {
          ...formData,
          discountType:
            formData.discountType === DiscountType.PERCENTAGE
              ? DiscountType.PERCENTAGE
              : DiscountType.FLAT,
          startDate: dayjs(formData.startDate).toISOString(),
          endDate: dayjs(
            new Date(formData.endDate).setHours(23, 59, 59, 999),
          ).toISOString(),
        };

        if (!formDataToSubmit.usageLimit) delete formDataToSubmit.usageLimit;
        if (!formDataToSubmit.usageLimitPerUser)
          delete formDataToSubmit.usageLimitPerUser;
        if (
          !formDataToSubmit.minimumSpend &&
          formDataToSubmit.minimumSpend !== 0
        )
          delete formDataToSubmit.minimumSpend;
        if (!formDataToSubmit.maximumDiscountValue)
          delete formDataToSubmit.maximumDiscountValue;

        formDataToSubmit.discountApplyFor = DiscountApplyForType.FLIGHT;

        await createDiscountService(formDataToSubmit);
        toast.success("Discount successfully created");
        navigation(ROUTE.FlightDiscountManagement);
      } catch (error: unknown) {
        if (
          error &&
          typeof error === "object" &&
          "status" in error &&
          error.status === 406
        ) {
          toast.error("Discount code already exists");
        } else if (
          error &&
          typeof error === "object" &&
          "status" in error &&
          error.status === 400 &&
          "response" in error
        ) {
          const axiosError = error as {
            response: { data: { message: string[] } };
          };
          const errorMessage = axiosError.response.data.message;
          errorMessage.forEach((message: string) => {
            toast.error(message);
          });
        } else {
          toast.error("Create discount failed!");
        }
      } finally {
        setLoading(false);
      }
    },
    [navigation],
  );

  const {
    handleChange: formikHandleChange,
    submitForm,
    errors,
    values,
    setFieldValue,
    validateField,
    setValues,
  } = useFormik<FlightDiscountForm>({
    initialValues: initial,
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: handleCreateDiscount,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (
      [
        "usageLimit",
        "usageLimitPerUser",
        "maximumDiscountValue",
        "discountValue",
        "minimumSpend",
      ].includes(name)
    ) {
      setFieldValue(name, value === "" ? "" : Number(value));
    } else {
      formikHandleChange(e);
    }
  };

  const handleDiscountTypeChange = (type: DiscountAmoutType) => {
    setSelectedDiscountType(type);
    setFieldValue("discountType", type);
    if (type === DiscountAmoutType.FIXED_AMOUNT) {
      setFieldValue("maximumDiscountValue", "");
    }
  };

  useEffect(() => {
    if (!date?.from || !date?.to) {
      setValues({
        ...values,
        startDate: "",
        endDate: "",
      });
      return;
    }
  
    setValues({
      ...values, 
      startDate: date.from.toISOString(),
      endDate: date.to.toISOString(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date?.from, date?.to]);

  useEffect(() => {
    if (refForm.current.discountName !== values.discountName) {
      validateField("discountName");
    }
    if (refForm.current.discountCode !== values.discountCode) {
      validateField("discountCode");
    }
    if (refForm.current.discountType !== values.discountType) {
      validateField("discountType");
    }
    if (refForm.current.discountValue !== values.discountValue) {
      validateField("discountValue");
    }
    if (refForm.current.startDate !== values.startDate) {
      validateField("startDate");
    }
    if (refForm.current.endDate !== values.endDate) {
      validateField("endDate");
    }
    if (refForm.current.usageLimit !== values.usageLimit) {
      validateField("usageLimit");
    }
    if (refForm.current.usageLimitPerUser !== values.usageLimitPerUser) {
      validateField("usageLimitPerUser");
    }
    if (refForm.current.minimumSpend !== values.minimumSpend) {
      validateField("minimumSpend");
    }
    if (refForm.current.maximumDiscountValue !== values.maximumDiscountValue) {
      validateField("maximumDiscountValue");
    }

    refForm.current = values;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  const baseInputClassName =
    "placeholder:text-[#94A3B8] text-sm h-12 rounded-lg border border-[#E2E8F0] bg-white px-3 text-[#020617] outline-none placeholder:text-sm placeholder:font-normal";

  return (
    <>
      <div className="-mt-16 mb-[14px] flex items-center gap-4 max-sm:mt-5 md:w-1/2 ">
        <IconButton
          aria-label="back"
          size="medium"
          sx={{
            border: "1px solid #E2E8F0",
            borderRadius: "8px",
          }}
          className=" size-10 max-sm:size-8 "
          onClick={() => navigation(ROUTE.FlightDiscountManagement)}
        >
          <img src="/icons/arrow-left.svg" alt="arrow-left" />
        </IconButton>

        <h2 className="text-3xl font-semibold text-text max-sm:text-lg ">
          Create Discount
        </h2>
      </div>

      <div className="mt-7 flex flex-col rounded-3xl bg-white p-5 text-[#1E293B] max-sm:mt-3 lg:px-8 lg:py-6">
        <div className="space-y-8 max-sm:space-y-5">
          <div className="text-xl font-semibold text-neutral-950 max-sm:text-lg">
            Condition
          </div>
          <div className="flex gap-8 max-sm:flex-col max-sm:gap-5">
            <div className="flex-1">
              <div className="flex w-full flex-col">
                <label
                  htmlFor="discountName"
                  className="mb-2 text-sm font-medium text-neutral-800"
                >
                  Discount Name
                </label>
                <input
                  id="discountName"
                  type="text"
                  name="discountName"
                  placeholder="Enter discount name"
                  className={cn(baseInputClassName)}
                  value={values.discountName}
                  onChange={handleChange}
                />
                {errors.discountName && (
                  <p className="mx-1 mt-1 text-[13px] text-red-500">
                    {errors.discountName}
                  </p>
                )}
              </div>
            </div>
            <div className="flex-1">
              <div className="flex w-full flex-col">
                <label
                  htmlFor="discountCode"
                  className="mb-2 text-sm font-medium text-neutral-800"
                >
                  Discount Code
                </label>
                <input
                  id="discountCode"
                  type="text"
                  name="discountCode"
                  placeholder="Enter discount code"
                  className={cn(baseInputClassName, "uppercase placeholder:normal-case")}
                  value={values.discountCode}
                  onChange={handleChange}
                />
                {errors.discountCode && (
                  <p className="mx-1 mt-1 text-[13px] text-red-500">
                    {errors.discountCode}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="flex gap-8 max-sm:flex-col max-sm:gap-5">
            <div className="flex-1">
              <div className="flex w-full flex-col">
                <label
                  htmlFor="usageLimit"
                  className="mb-2 text-sm font-medium text-neutral-800"
                >
                  Usage per Code
                </label>
                <input
                  id="usageLimit"
                  type="number"
                  name="usageLimit"
                  placeholder="Enter usage per Code"
                  className={cn(baseInputClassName)}
                  onChange={handleChange}
                />
                {errors.usageLimit && (
                  <p className="mx-1 mt-1 text-[13px] text-red-500">
                    {errors.usageLimit}
                  </p>
                )}
              </div>
              <div className="mt-3 flex items-center gap-2 text-sm text-[#64748B]">
                If left empty, the discount can be applied an unlimited number
                of times.
              </div>
            </div>
            <div className="flex-1">
              <div className="flex w-full flex-col">
                <label
                  htmlFor="usageLimitPerUser"
                  className="mb-2 text-sm font-medium text-neutral-800"
                >
                  Usage per User
                </label>
                <input
                  id="usageLimitPerUser"
                  type="number"
                  name="usageLimitPerUser"
                  placeholder="Enter usage per User"
                  className={cn(baseInputClassName)}
                  onChange={handleChange}
                />
                {errors.usageLimitPerUser && (
                  <p className="mx-1 mt-1 text-[13px] text-red-500">
                    {errors.usageLimitPerUser}
                  </p>
                )}
              </div>
              <div className="mt-3 flex items-center gap-2 text-sm text-[#64748B]">
                If left empty, the discount can be applied an unlimited number
                of times.
              </div>
            </div>
          </div>

          <div className="flex gap-8 max-sm:flex-col max-sm:gap-5">
            <div className="flex-1">
              <div className="flex w-full flex-col">
                <label
                  htmlFor="minimumSpend"
                  className="mb-2 text-sm font-medium"
                >
                  Minimum Booking Amount
                </label>
                <div
                  className={cn(baseInputClassName, "relative overflow-hidden")}
                >
                  <input
                    id="minimumSpend"
                    type="number"
                    name="minimumSpend"
                    placeholder="Enter amount"
                    className={cn(
                      "h-full w-[85%] bg-white outline-none placeholder:text-[#94A3B8]",
                    )}
                    onChange={handleChange}
                    value={values.minimumSpend ?? ""}
                  />
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex w-[68px] items-center justify-center border-l bg-[#F6F9FB] max-sm:w-12">
                    <span className="text-[16px] font-semibold text-[#94A3B8]">
                      USD
                    </span>
                  </div>
                </div>
                {errors.minimumSpend && (
                  <p className="mx-1 mt-1 text-[13px] text-red-500">
                    {errors.minimumSpend}
                  </p>
                )}
              </div>
            </div>
            <div className="flex-1">
              <div className="flex w-full flex-col">
                <label htmlFor="validDate" className="mb-2 text-sm font-medium">
                  Valid Date
                </label>
                <SelectDiscountDate
                  mode="range"
                  {...propsDate}
                  date={date}
                  setDateSelect={propsDate.setDate}
                  numberOfMonths={isMobile ? 1 : 2}
                  contentStyle={{
                    width: isMobile ? "var(--radix-popover-trigger-width)" : "",
                  }}
                />
                {(errors.startDate || errors.endDate) && (
                  <p className="mx-1 mt-1 text-[13px] text-red-500">
                    {errors.startDate}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12 space-y-6 max-sm:mt-4 max-sm:space-y-5">
          <div className="text-xl font-semibold text-neutral-950">Action</div>
          <div className="flex gap-8 max-sm:flex-col max-sm:gap-5">
            <div className="flex-1">
              <div className="flex w-full flex-col">
                <label
                  htmlFor="discountAmount"
                  className="mb-2 text-sm font-medium"
                >
                  Discount Amount
                </label>
                <div
                  className={cn(
                    baseInputClassName,
                    "relative flex overflow-hidden p-0",
                  )}
                >
                  <div className="flex w-[90%] items-center p-2 max-sm:w-[85%] max-sm:gap-2">
                    <div className="max-sm:w-2/5">
                      <Popover open={isOpen} onOpenChange={setIsOpen}>
                        <PopoverTrigger>
                          <div className="flex items-center justify-between bg-button-background px-4 py-2 text-neutral-950 max-sm:w-full max-sm:gap-1 max-sm:rounded-[8px] max-sm:px-2">
                            <span className="max-sm:text-xs">
                              {selectedDiscountType}
                            </span>
                            <ChevronDown className="size-4 max-sm:size-3" />
                          </div>
                        </PopoverTrigger>
                        <PopoverContent align="start" className="w-[125px]">
                          <div className="flex cursor-pointer flex-col gap-2">
                            <div
                              onClick={() => {
                                handleDiscountTypeChange(
                                  DiscountAmoutType.FIXED_AMOUNT,
                                );
                                setIsOpen(false);
                              }}
                            >
                              Fixed
                            </div>
                            <div
                              onClick={() => {
                                handleDiscountTypeChange(
                                  DiscountAmoutType.PERCENTAGE,
                                );
                                setIsOpen(false);
                              }}
                            >
                              Percentage
                            </div>
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>

                    <input
                      id="discountValue"
                      type="number"
                      name="discountValue"
                      placeholder="Enter amount"
                      className={cn(
                        "h-full flex-1 bg-white px-3 outline-none placeholder:text-[#94A3B8] max-sm:ml-auto max-sm:w-2/5",
                      )}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="pointer-events-none ml-auto flex w-[68px] items-center justify-center border-l bg-[#F6F9FB] max-sm:w-12">
                    <span className="text-[16px] font-semibold text-[#94A3B8]">
                      {selectedDiscountType === DiscountAmoutType.PERCENTAGE
                        ? "%"
                        : "USD"}
                    </span>
                  </div>
                </div>
                {errors.discountValue && (
                  <p className="mx-1 mt-1 text-[13px] text-red-500">
                    {errors.discountValue}
                  </p>
                )}
              </div>
            </div>
            <div className="flex-1">
              <div className="flex w-full flex-col">
                <label
                  htmlFor="maximumDiscountValue"
                  className="mb-2 text-sm font-medium"
                >
                  Maximum Discount
                </label>
                <input
                  id="maximumDiscountValue"
                  type="number"
                  name="maximumDiscountValue"
                  placeholder="Enter maximum amount"
                  className={cn(
                    baseInputClassName,
                    selectedDiscountType === DiscountAmoutType.FIXED_AMOUNT
                      ? "cursor-not-allowed bg-neutral-50"
                      : "",
                  )}
                  onChange={handleChange}
                  value={values.maximumDiscountValue ?? ""}
                  disabled={
                    selectedDiscountType === DiscountAmoutType.FIXED_AMOUNT
                  }
                />

                {errors.maximumDiscountValue && (
                  <p className="mx-1 mt-1 text-[13px] text-red-500">
                    {errors.maximumDiscountValue}
                  </p>
                )}
              </div>
            </div>
          </div>
          <div className="flex w-full items-center justify-end gap-4">
            <button
              className="h-12 w-[171px] rounded-[10px] bg-button-background text-base font-medium text-text outline-none max-sm:h-10 max-sm:w-1/2"
              onClick={() => navigation(ROUTE.FlightDiscountManagement)}
            >
              Cancel
            </button>
            <button
              onClick={submitForm}
              className="flex h-12 w-[191px] items-center justify-center rounded-xl bg-text text-white max-sm:h-10 max-sm:w-1/2"
              disabled={isLoading}
            >
              <span className="text-base font-medium">
                Create Discount
                {isLoading && (
                  <CircularProgress
                    size="16px"
                    className="ml-3"
                    color="inherit"
                  />
                )}
              </span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateFlightDiscountPage;
